import { COMMANDS_BY_NAME, type Command, type OutputLine } from "./terminal-content.js";

const SCROLL_STEP = 60;
const TYPE_SPEED_MS = 30;
const LINE_DELAY_MS = 45;
const BLOCK_PAUSE_MS = 300;

const PS1 =
  '<span class="ps1">josh<span class="ps1-at">@</span>site<span class="ps1-colon">:</span><span class="ps1-path">~</span><span class="ps1-dollar">$</span></span> ';

interface Block {
  element: HTMLElement;
  command: Command;
  promptLine: HTMLElement;
  outputElement: HTMLElement;
  typed: boolean;
  typing: boolean;
}

type Mode = "normal" | "search";

let blocks: Block[] = [];
let links: HTMLElement[] = [];
let currentLink = -1;
let mode: Mode = "normal";
let nextBlock = 0;
let waitingForScroll = false;
let gPending = false;
let scrollHeld: 1 | -1 | 0 = 0;
let scrollFrame = 0;
let serverStartSeconds: number | null = null;
let uptimeElement: HTMLElement | null = null;
let searchQuery = "";
let searchMatches: HTMLElement[] = [];
let currentMatch = -1;

/** Boots the scripted terminal and its vim-style navigation. */
export function initTerminal(): void {
  blocks = [];

  for (const element of Array.from(document.querySelectorAll<HTMLElement>(".cmd-block[data-cmd]"))) {
    const command = COMMANDS_BY_NAME.get(element.dataset.cmd ?? "");
    if (!command) continue;

    element.innerHTML = "";

    const promptLine = document.createElement("div");
    promptLine.className = "cmd-prompt";
    promptLine.innerHTML = `${PS1}<span class="cmd-typed"></span><span class="cmd-cursor"></span>`;
    element.appendChild(promptLine);

    const outputElement = document.createElement("div");
    outputElement.className = "cmd-output";
    element.appendChild(outputElement);

    blocks.push({ element, command, promptLine, outputElement, typed: false, typing: false });
  }

  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);
  window.addEventListener("scroll", onScroll, { passive: true });

  createSearchBar();
  createCommandLine();
  createTooltip();
  updateMode();
  updatePosition();
  void loadServerInfo();

  window.setTimeout(advanceQueue, 600);
}

// -------------------------------------------------------------------- typing

function advanceQueue(): void {
  const block = blocks[nextBlock];
  if (!block || block.typed || block.typing) return;

  if (!isVisible(block.element)) {
    waitingForScroll = true;
    return;
  }

  waitingForScroll = false;
  typeCommand(block, () => {
    nextBlock++;
    window.setTimeout(advanceQueue, BLOCK_PAUSE_MS);
  });
}

function isVisible(element: HTMLElement): boolean {
  return element.getBoundingClientRect().top < window.innerHeight - 60;
}

function typeCommand(block: Block, done: () => void): void {
  block.typing = true;
  const typed = block.promptLine.querySelector<HTMLElement>(".cmd-typed");
  const cursor = block.promptLine.querySelector<HTMLElement>(".cmd-cursor");
  const text = block.command.command;
  let index = 0;

  const timer = window.setInterval(() => {
    if (index < text.length) {
      if (typed) typed.textContent += text[index];
      index++;
      return;
    }
    window.clearInterval(timer);
    cursor?.classList.add("done");
    window.setTimeout(() => revealOutput(block, done), 120);
  }, TYPE_SPEED_MS);
}

function revealOutput(block: Block, done: () => void): void {
  let index = 0;

  const showNext = () => {
    if (index >= block.command.output.length) {
      block.typed = true;
      block.typing = false;
      rebuildLinks();
      attachUptime();
      done();
      return;
    }

    block.outputElement.appendChild(renderLine(block.command.output[index]!));
    index++;
    window.setTimeout(showNext, LINE_DELAY_MS);
  };

  showNext();
}

function renderLine(line: OutputLine): HTMLElement {
  const element = document.createElement("div");
  element.className = `out ${line.className ?? ""}`.trim();

  // Only the curated content in terminal-content.ts is ever inserted as HTML.
  if (line.html) element.innerHTML = line.html;
  else if (line.text) element.textContent = line.text;

  element.style.opacity = "0";
  requestAnimationFrame(() => {
    element.style.transition = "opacity 0.2s ease";
    element.style.opacity = "1";
  });

  return element;
}

function rebuildLinks(): void {
  links = Array.from(document.querySelectorAll<HTMLElement>(".json-val-link"));
  for (const link of links) {
    if (link.dataset.wired) continue;
    link.dataset.wired = "1";
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const href = link.dataset.href;
      if (href) window.open(href, href.startsWith("mailto:") ? "_self" : "_blank", "noopener");
    });
  }
}

// -------------------------------------------------------------------- uptime

async function loadServerInfo(): Promise<void> {
  try {
    const response = await fetch("/api/info");
    const info = (await response.json()) as { bootTime?: number };
    if (typeof info.bootTime === "number") {
      serverStartSeconds = info.bootTime;
      tickUptime();
    }
  } catch {
    // The uptime line simply stays on "loading..." if the API is unreachable.
  }

  void loadLoginLine();
}

/**
 * The "current login" line is a small flourish; it is cached for a day so a
 * repeat visit does not call out to the geolocation service again.
 */
async function loadLoginLine(): Promise<void> {
  const line = document.getElementById("login-line");
  if (!line) return;

  const KEY = "geo-cache";
  const TTL = 24 * 60 * 60 * 1000;

  const apply = (ip: string, city: string, country: string) => {
    const place = city && country ? ` (${city}, ${country})` : city ? ` (${city})` : "";
    line.textContent = ip ? `Current login: ${ip}${place}` : "Current login: unknown";
  };

  try {
    const cached = JSON.parse(localStorage.getItem(KEY) ?? "null") as
      | { ip: string; city: string; country: string; at: number }
      | null;
    if (cached && Date.now() - cached.at < TTL) {
      apply(cached.ip, cached.city, cached.country);
      return;
    }
  } catch {
    // A corrupt cache entry is simply refetched.
  }

  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = (await response.json()) as { ip?: string; city?: string; country_name?: string };
    const ip = data.ip ?? "";
    const city = data.city ?? "";
    const country = data.country_name ?? "";
    if (ip) {
      try {
        localStorage.setItem(KEY, JSON.stringify({ ip, city, country, at: Date.now() }));
      } catch {
        // Storage being unavailable does not stop the line from rendering.
      }
    }
    apply(ip, city, country);
  } catch {
    line.textContent = "Current login: unknown";
  }
}

function tickUptime(): void {
  if (!uptimeElement || serverStartSeconds === null) return;
  const elapsed = Math.max(0, Date.now() / 1000 - serverStartSeconds);
  uptimeElement.textContent = `${Math.floor(elapsed).toLocaleString()}s`;
  requestAnimationFrame(tickUptime);
}

function attachUptime(): void {
  if (uptimeElement) return;
  uptimeElement = document.getElementById("neofetch-uptime");
  if (!uptimeElement) return;

  const tooltip = createTooltip();
  const show = (event: MouseEvent) => {
    if (serverStartSeconds === null) return;
    tooltip.textContent = humaniseUptime(Math.max(0, Date.now() / 1000 - serverStartSeconds));
    tooltip.classList.add("visible");
    place(event);
  };
  const place = (event: MouseEvent) => {
    tooltip.style.left = `${event.clientX}px`;
    tooltip.style.top = `${event.clientY - 8}px`;
  };

  uptimeElement.addEventListener("mouseenter", show);
  uptimeElement.addEventListener("mousemove", place);
  uptimeElement.addEventListener("mouseleave", () => tooltip.classList.remove("visible"));

  if (serverStartSeconds !== null) tickUptime();
}

function humaniseUptime(seconds: number): string {
  const years = Math.floor(seconds / 31536000);
  const days = Math.floor((seconds % 31536000) / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`);
  parts.push(`${Math.floor(seconds % 60)}s`);
  return parts.join(", ");
}

function createTooltip(): HTMLElement {
  const existing = document.getElementById("uptime-tooltip");
  if (existing) return existing;
  const tooltip = document.createElement("div");
  tooltip.id = "uptime-tooltip";
  document.body.appendChild(tooltip);
  return tooltip;
}

// ------------------------------------------------------------------ movement

function onScroll(): void {
  updatePosition();
  if (waitingForScroll) advanceQueue();
}

function handleKeyDown(event: KeyboardEvent): void {
  const container = document.getElementById("game-container");
  if (container?.classList.contains("fullscreen") || container?.classList.contains("expanding")) return;

  const clockDetail = document.getElementById("clock-detail");
  if (clockDetail && !clockDetail.classList.contains("hidden")) return;

  if (mode === "search") {
    handleSearchKey(event);
    return;
  }

  if (gPending) {
    gPending = false;
    if (event.key === "g") {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
  }

  switch (event.key) {
    case "j":
      event.preventDefault();
      startScrolling(1);
      break;
    case "k":
      event.preventDefault();
      startScrolling(-1);
      break;
    case "d":
      if (event.ctrlKey) {
        event.preventDefault();
        window.scrollBy(0, window.innerHeight * 0.45);
      }
      break;
    case "u":
      if (event.ctrlKey) {
        event.preventDefault();
        window.scrollBy(0, -window.innerHeight * 0.45);
      }
      break;
    case "G":
      event.preventDefault();
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      break;
    case "g":
      gPending = true;
      window.setTimeout(() => (gPending = false), 500);
      break;
    case "Tab":
      event.preventDefault();
      focusLink(event.shiftKey ? -1 : 1);
      break;
    case "Enter":
      event.preventDefault();
      links[currentLink]?.click();
      break;
    case "/":
      event.preventDefault();
      enterSearchMode();
      break;
    case "n":
      stepMatch(1);
      break;
    case "N":
      stepMatch(-1);
      break;
    default:
      break;
  }
}

function handleKeyUp(event: KeyboardEvent): void {
  if (event.key === "j" || event.key === "k") stopScrolling();
}

function startScrolling(direction: 1 | -1): void {
  if (scrollHeld === direction) return;
  scrollHeld = direction;
  if (scrollFrame) return;

  let last = 0;
  const step = (now: number) => {
    if (scrollHeld === 0) {
      scrollFrame = 0;
      return;
    }
    if (last) window.scrollBy(0, (SCROLL_STEP * scrollHeld * Math.min(now - last, 50)) / 16);
    last = now;
    scrollFrame = requestAnimationFrame(step);
  };
  scrollFrame = requestAnimationFrame(step);
}

function stopScrolling(): void {
  scrollHeld = 0;
  if (scrollFrame) cancelAnimationFrame(scrollFrame);
  scrollFrame = 0;
}

function focusLink(direction: 1 | -1): void {
  if (links.length === 0) return;
  for (const link of links) link.classList.remove("vim-selected");

  currentLink = (currentLink + direction + links.length) % links.length;
  const link = links[currentLink];
  link?.classList.add("vim-selected");
  link?.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function updatePosition(): void {
  let index = 0;
  for (let i = blocks.length - 1; i >= 0; i--) {
    if (blocks[i]!.element.getBoundingClientRect().top <= 150) {
      index = i;
      break;
    }
  }
  const position = document.getElementById("vb-pos");
  if (position) position.textContent = `${index + 1}:1`;
}

function updateMode(): void {
  const element = document.getElementById("vb-mode");
  if (!element) return;
  element.textContent = mode === "search" ? "SEARCH" : "NORMAL";
  element.className = `vb-mode${mode === "search" ? " search" : ""}`;
}

// -------------------------------------------------------------------- search

function createSearchBar(): void {
  if (document.getElementById("vim-search-bar")) return;
  const bar = document.createElement("div");
  bar.id = "vim-search-bar";
  bar.innerHTML =
    '<span class="search-prefix">/</span><input type="text" id="vim-search-input" autocomplete="off" spellcheck="false">';
  document.body.appendChild(bar);
}

function createCommandLine(): void {
  if (document.getElementById("vim-cmdline")) return;
  const line = document.createElement("div");
  line.id = "vim-cmdline";
  document.body.appendChild(line);
}

function enterSearchMode(): void {
  mode = "search";
  document.getElementById("vim-search-bar")?.classList.add("active");
  const input = document.getElementById("vim-search-input") as HTMLInputElement | null;
  if (input) {
    input.value = "";
    input.focus();
  }
  updateMode();
}

function exitSearchMode(): void {
  mode = "normal";
  document.getElementById("vim-search-bar")?.classList.remove("active");
  (document.getElementById("vim-search-input") as HTMLInputElement | null)?.blur();
  updateMode();
}

function handleSearchKey(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    clearHighlights();
    exitSearchMode();
    return;
  }
  if (event.key !== "Enter") return;

  event.preventDefault();
  searchQuery = (document.getElementById("vim-search-input") as HTMLInputElement | null)?.value ?? "";
  runSearch();
  exitSearchMode();
}

function runSearch(): void {
  clearHighlights();
  if (!searchQuery) return;

  const scope = document.getElementById("term-scroll");
  if (!scope) return;

  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);

  const pattern = new RegExp(escapeRegex(searchQuery), "gi");

  for (const node of nodes) {
    const text = node.textContent ?? "";
    pattern.lastIndex = 0;

    const fragments: Node[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragments.push(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      const mark = document.createElement("mark");
      mark.className = "search-match";
      mark.textContent = match[0];
      fragments.push(mark);
      searchMatches.push(mark);
      lastIndex = pattern.lastIndex;
      if (match[0].length === 0) pattern.lastIndex++;
    }

    if (fragments.length === 0) continue;
    if (lastIndex < text.length) fragments.push(document.createTextNode(text.slice(lastIndex)));

    const wrapper = document.createDocumentFragment();
    for (const fragment of fragments) wrapper.appendChild(fragment);
    node.parentNode?.replaceChild(wrapper, node);
  }

  if (searchMatches.length > 0) {
    currentMatch = 0;
    highlightCurrent();
  }
  showCommandLine(
    `/${searchQuery}  [${searchMatches.length} match${searchMatches.length === 1 ? "" : "es"}]`,
  );
}

function stepMatch(direction: 1 | -1): void {
  if (searchMatches.length === 0) return;
  currentMatch = (currentMatch + direction + searchMatches.length) % searchMatches.length;
  highlightCurrent();
}

function highlightCurrent(): void {
  for (const match of searchMatches) match.classList.remove("current");
  const match = searchMatches[currentMatch];
  if (!match) return;
  match.classList.add("current");
  match.scrollIntoView({ behavior: "smooth", block: "center" });
  showCommandLine(`/${searchQuery}  [${currentMatch + 1}/${searchMatches.length}]`);
}

function clearHighlights(): void {
  for (const mark of Array.from(document.querySelectorAll<HTMLElement>("mark.search-match"))) {
    const parent = mark.parentNode;
    if (!parent) continue;
    parent.replaceChild(document.createTextNode(mark.textContent ?? ""), mark);
    parent.normalize();
  }
  searchMatches = [];
  currentMatch = -1;
  document.getElementById("vim-cmdline")?.classList.remove("active");
}

function showCommandLine(text: string): void {
  const element = document.getElementById("vim-cmdline");
  if (!element) return;
  element.textContent = text;
  element.classList.add("active");
}

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
